import { FixaInputField } from "@coworker/apprestructured/src/shared/wrappers/FixaInputField/FixaInputField";
import {
  FixaOption,
  FixaSelect,
} from "@coworker/apprestructured/src/shared/wrappers/FixaSelect/FixaSelect";
import { getDepartmentsOptions } from "@coworker/app/src/helpers/departmentsOptions";
import { getLivingSituationOptions } from "@coworker/app/src/helpers/livingSituationOptions";
import { getLongTermPriorityOptions } from "@coworker/app/src/helpers/longTermPriorityOptions";
import { getPricingDataOptions } from "@coworker/app/src/helpers/pricingDataOptions";
import { getStyleGroupOptions } from "@coworker/app/src/helpers/styleGroupOptions";
import { useTranslation } from "@coworker/locales";
import { useMemo, useState } from "react";
import styles from "./media-about-fields.module.css";

type Option = {
  value: string;
  name: string;
};

type FieldType = "select" | "text";

export type MediaAboutFieldKey =
  | "focus"
  | "livingSituation"
  | "styleGroup"
  | "longTermPriority"
  | "pricing"
  | "location"
  | "rangeDescription"
  | "productRange"
  | "firstGrouping"
  | "secondGrouping"
  | "thirdGrouping"
  | "merchandisingPa"
  | "colorAndNcs";

// Backend mappers should convert API payloads into these UI-facing keys.
export type MediaAboutFieldValues = Partial<Record<MediaAboutFieldKey, string>>;

type FieldConfig = {
  type: FieldType;
  label: string;
  options?: Option[];
  fallbackValue: string;
};

type MediaAboutFieldsProps = {
  mediaType: string;
  location?: string;
  initialValues?: MediaAboutFieldValues;
  values?: MediaAboutFieldValues;
  onChange?: (values: MediaAboutFieldValues) => void;
};

const focusOptions: Option[] = [
  {
    value: "Complete cooking and eating",
    name: "Complete cooking and eating",
  },
];

const groupingOptions: Option[] = ["Color / Material", "Style", "Family"].map(
  (value) => ({
    value,
    name: value,
  })
);

const groupingFields: MediaAboutFieldKey[] = [
  "firstGrouping",
  "secondGrouping",
  "thirdGrouping",
];

const homeFurnishingSolutionFields: MediaAboutFieldKey[] = [
  "focus",
  "livingSituation",
  "styleGroup",
  "longTermPriority",
  "pricing",
  "location",
];

const combineCoordinateRevitalizeFields: MediaAboutFieldKey[] = [
  "focus",
  "styleGroup",
  "longTermPriority",
  "pricing",
  "location",
];

const compareAndChooseFields: MediaAboutFieldKey[] = [
  "focus",
  "rangeDescription",
  "productRange",
  "firstGrouping",
  "secondGrouping",
  "thirdGrouping",
  "location",
];

const merchandisingFields: MediaAboutFieldKey[] = [
  "focus",
  "merchandisingPa",
  "colorAndNcs",
  "location",
];

const fieldsByMediaType: Record<string, MediaAboutFieldKey[]> = {
  Home: homeFurnishingSolutionFields,
  "Room setting": homeFurnishingSolutionFields,
  Vignette: homeFurnishingSolutionFields,
  "Coordination media": ["focus", "styleGroup", "longTermPriority", "location"],
  "Vitality media": combineCoordinateRevitalizeFields,
  "Cross over media": combineCoordinateRevitalizeFields,
  Showcase: combineCoordinateRevitalizeFields,
  "Perimeter break": ["focus", "styleGroup", "longTermPriority", "location"],
  "Statement media": ["focus", "styleGroup", "longTermPriority", "location"],
  "All parts studio": compareAndChooseFields,
  "Display compact": compareAndChooseFields,
  "Product dominance wall": merchandisingFields,
  "Selection point": merchandisingFields,
  "Front media": merchandisingFields,
  "Open-the-wallet": merchandisingFields,
  "Opening offer": merchandisingFields,
};

const uniqueOptions = (options: Option[]) => {
  const seen = new Set<string>();
  return options.filter(({ value }) => {
    if (seen.has(value)) {
      return false;
    }

    seen.add(value);
    return true;
  });
};

export const MediaAboutFields = ({
  mediaType,
  location,
  initialValues,
  values,
  onChange,
}: MediaAboutFieldsProps) => {
  const { t } = useTranslation();
  const [localValues, setLocalValues] = useState<MediaAboutFieldValues>(
    initialValues ?? {}
  );

  const currentValues = values ?? localValues;

  const locationOptions = useMemo(
    () =>
      uniqueOptions([
        ...(location ? [{ value: location, name: location }] : []),
        ...getDepartmentsOptions(t),
      ]),
    [location, t]
  );

  const fieldConfigs: Record<MediaAboutFieldKey, FieldConfig> = useMemo(
    () => ({
      focus: {
        type: "select",
        label: t("focusString", { defaultValue: "Focus" }),
        options: focusOptions,
        fallbackValue: focusOptions[0]?.value ?? "",
      },
      livingSituation: {
        type: "select",
        label: t("livingSituationString"),
        options: getLivingSituationOptions(),
        fallbackValue: "Living together",
      },
      styleGroup: {
        type: "select",
        label: t("styleGroupString"),
        options: getStyleGroupOptions(),
        fallbackValue: "International modern",
      },
      longTermPriority: {
        type: "select",
        label: t("longtermPriorityString"),
        options: getLongTermPriorityOptions(),
        fallbackValue: "Organise your living",
      },
      pricing: {
        type: "select",
        label: t("priceLevelString"),
        options: [
          { value: "Not specified", name: t("notSpecifiedString") },
          ...getPricingDataOptions(),
        ],
        fallbackValue: "Not specified",
      },
      location: {
        type: "select",
        label: t("hejProfileLabelLocation"),
        options: locationOptions,
        fallbackValue: location ?? "",
      },
      rangeDescription: {
        type: "text",
        label: t("rangeDescription2String"),
        fallbackValue: "",
      },
      productRange: {
        type: "text",
        label: t("productRangeString"),
        fallbackValue: "",
      },
      firstGrouping: {
        type: "select",
        label: t("firstGroupingString"),
        options: groupingOptions,
        fallbackValue: groupingOptions[0]?.value ?? "",
      },
      secondGrouping: {
        type: "select",
        label: t("secondGroupingString"),
        options: groupingOptions,
        fallbackValue: groupingOptions[1]?.value ?? "",
      },
      thirdGrouping: {
        type: "select",
        label: t("thirdGroupingString"),
        options: groupingOptions,
        fallbackValue: groupingOptions[2]?.value ?? "",
      },
      merchandisingPa: {
        type: "text",
        label: t("merchandisingPaString"),
        // TODO: Replace with a backend-backed PA dropdown, matching RoomSettings MerchandisingPaSelect.
        fallbackValue: "",
      },
      colorAndNcs: {
        type: "text",
        label: t("colorAndNcsString"),
        fallbackValue: "",
      },
    }),
    [location, locationOptions, t]
  );

  const fields = fieldsByMediaType[mediaType] ?? ["focus", "location"];

  const onFieldChange = (field: MediaAboutFieldKey, value: string) => {
    const previousDisplayedValue =
      currentValues[field] ?? fieldConfigs[field].fallbackValue;
    const nextValues = {
      ...currentValues,
      [field]: value,
    };

    if (groupingFields.includes(field)) {
      groupingFields
        .filter((groupingField) => groupingField !== field)
        .forEach((groupingField) => {
          const displayedValue =
            currentValues[groupingField] ??
            fieldConfigs[groupingField].fallbackValue;

          if (displayedValue === value) {
            nextValues[groupingField] = previousDisplayedValue;
          }
        });
    }

    if (!values) {
      setLocalValues(nextValues);
    }

    onChange?.(nextValues);
  };

  return (
    <section className={styles["fieldsGrid"]} aria-label="About media details">
      {fields.map((field) => {
        const config = fieldConfigs[field];
        const value = currentValues[field] ?? config.fallbackValue;
        const id = `launch-media-about-${field}`;

        return (
          <div className={styles["field"]} key={field}>
            {config.type === "select" ? (
              <FixaSelect
                id={id}
                label={config.label}
                value={value}
                onChange={(event) => onFieldChange(field, event.target.value)}
              >
                {(config.options ?? []).map((option) => (
                  <FixaOption
                    key={option.value}
                    value={option.value}
                    name={option.name}
                  />
                ))}
              </FixaSelect>
            ) : (
              <FixaInputField
                id={id}
                type="text"
                label={config.label}
                value={value}
                onChange={(event) => onFieldChange(field, event.target.value)}
              />
            )}
          </div>
        );
      })}
    </section>
  );
};
